import { useState, useEffect, useCallback } from "react";
import { axiosInstance } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Ticket, Save, Box, UserCog } from "lucide-react";

export default function LotteryAccess({ user }) {
  const [boxes, setBoxes] = useState([]);
  const [masters, setMasters] = useState([]);
  const [enabledBoxes, setEnabledBoxes] = useState(new Set());
  const [enabledMasters, setEnabledMasters] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchAccess = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get("/lottery/access");
      setBoxes(r.data.boxes || []);
      setMasters(r.data.masters || []);
      setEnabledBoxes(new Set((r.data.boxes || []).filter((b) => b.lottery_enabled).map((b) => b.id)));
      setEnabledMasters(new Set((r.data.masters || []).filter((m) => m.lottery_enabled).map((m) => m.id)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el acceso");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccess(); }, [fetchAccess]);

  const toggle = (set, setSet, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSet(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axiosInstance.post("/lottery/access", {
        enabled_box_ids: Array.from(enabledBoxes),
        enabled_master_ids: Array.from(enabledMasters),
      });
      toast.success("Acceso a Apuestas actualizado");
      fetchAccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!user?.is_super_master) {
    return (
      <div className="p-8 text-center text-slate-500" data-testid="lottery-access-denied">
        Solo el Super Master puede administrar el acceso a Apuestas y Sorteos.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lottery-access-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-100 text-amber-700"><Ticket className="w-6 h-6" /></div>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "Space Grotesk, sans-serif" }}>Acceso a Apuestas y Sorteos</h1>
            <p className="text-sm text-slate-500">Habilita el módulo por caja y por master. Se requiere que <strong>ambos</strong> estén habilitados.</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving || loading} data-testid="lottery-access-save-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Guardando..." : "Guardar cambios"}
        </Button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-400">Cargando...</div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          <Card data-testid="lottery-access-masters-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg"><UserCog className="w-5 h-5" /> Masters</CardTitle>
              <CardDescription>Marque los masters que pueden usar el módulo.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {masters.map((m) => (
                <label
                  key={m.id}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 cursor-pointer"
                  data-testid={`lottery-master-row-${m.id}`}
                >
                  <Checkbox
                    checked={m.is_super_master || enabledMasters.has(m.id)}
                    disabled={m.is_super_master}
                    onCheckedChange={() => toggle(enabledMasters, setEnabledMasters, m.id)}
                    data-testid={`lottery-master-checkbox-${m.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{m.username}</div>
                    <div className="text-xs text-slate-400 truncate">{m.email || "—"}</div>
                  </div>
                  {m.is_super_master && <span className="text-xs text-amber-600 font-medium">Super Master</span>}
                </label>
              ))}
              {masters.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">Sin masters.</div>}
            </CardContent>
          </Card>

          <Card data-testid="lottery-access-boxes-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg"><Box className="w-5 h-5" /> Cajas</CardTitle>
              <CardDescription>Marque las cajas donde se puede usar el módulo.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {boxes.map((b) => (
                <label
                  key={b.id}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 cursor-pointer"
                  data-testid={`lottery-box-row-${b.id}`}
                >
                  <Checkbox
                    checked={enabledBoxes.has(b.id)}
                    onCheckedChange={() => toggle(enabledBoxes, setEnabledBoxes, b.id)}
                    data-testid={`lottery-box-checkbox-${b.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{b.name}</div>
                  </div>
                </label>
              ))}
              {boxes.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">Sin cajas.</div>}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
